import { Fragment, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Ban, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { SortableHeader } from '../ui/SortableHeader';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { formatDate } from '../../utils/format';
import type { GroupRow, GroupSortField } from '../../types/customerDuplicates';
import { parseCompanyCodes } from '../../types/customerDuplicates';
import { GroupDetailPanel } from './GroupDetailPanel';

interface Props {
    rows: GroupRow[];
    total: number;
    loading: boolean;
    activeFilterCount: number;
    sortBy: GroupSortField;
    sortOrder: 'asc' | 'desc';
    onSort: (field: GroupSortField) => void;
    overdueDays: number;
    /** Opened on mount when the URL carried `?customer=` or `?group=`. */
    initialOpenGroupId?: number | null;
}

interface Col {
    id: string;
    label: string;
    sort?: GroupSortField;
    align?: 'right';
    width: number;
}

const WIDTH_KEY = 'customer-duplicate-groups-col-widths';

const COLS: Col[] = [
    { id: 'expand', label: '', width: 28 },
    { id: 'warning', label: 'Warning', sort: 'warning', width: 110 },
    { id: 'name', label: 'Customer', sort: 'name', width: 260 },
    { id: 'companies', label: 'Exists in', sort: 'companies', width: 190 },
    { id: 'members', label: 'Accounts', sort: 'members', align: 'right', width: 80 },
    { id: 'openAmount', label: 'Total open AR', sort: 'openAmount', align: 'right', width: 130 },
    { id: 'overdueAmount', label: 'Overdue', sort: 'overdueAmount', align: 'right', width: 120 },
    { id: 'revenue', label: 'Revenue', sort: 'revenue', align: 'right', width: 130 },
    { id: 'dunning', label: 'Dunning', width: 80 },
    { id: 'lastMovement', label: 'Last mvmt', sort: 'lastMovement', width: 100 },
    { id: 'hubspot', label: 'CRM', align: 'right', width: 60 },
];

function eur(n: number): string {
    return Number(n ?? 0).toLocaleString('de-CH', { maximumFractionDigits: 0 });
}

/**
 * The group work list. "Diesen Kunden gibt es in dch, dit, dsp" is the `Exists in` column; the
 * total balance is `Total open AR`, and the two warning icons are the whole point of the view.
 *
 * `blockedSplit` / `overdueSplit` arrive from SQL as BIT, i.e. as NUMBERS — `Boolean(...)` and
 * never a bare `&&`, or a falsy 0 renders as a literal "0" next to the customer name. That is
 * the P3 defect (handoff P3 § 4.1), and every BIT column below is treated the same way.
 */
export function GroupsTable({
    rows, total, loading, activeFilterCount, sortBy, sortOrder, onSort, overdueDays, initialOpenGroupId,
}: Props) {
    const [expandedId, setExpandedId] = useState<number | null>(null);
    // A deep link arrives BEFORE the rows do, so the group it names cannot be the initial state —
    // it has to be applied once the row that matches it exists. Applied once per id, so a reader
    // who then collapses the panel does not have it spring open again on the next render.
    const openedDeepLink = useRef<number | null>(null);
    useEffect(() => {
        if (initialOpenGroupId == null || openedDeepLink.current === initialOpenGroupId) return;
        openedDeepLink.current = initialOpenGroupId;
        setExpandedId(initialOpenGroupId);
    }, [initialOpenGroupId]);
    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLS.map(c => ({ key: c.id, width: c.width })), WIDTH_KEY);
    const totalWidth = totalWidthOf(COLS.map(c => c.id));

    const sortState = { key: sortBy, direction: sortOrder };

    return (
        <div className="rounded-lg border border-gray-200 bg-white">
            {loading && rows.length === 0 ? (
                <div className="flex h-48 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
            ) : rows.length === 0 ? (
                <div className="flex h-48 items-center justify-center px-6 text-center">
                    <p className="text-sm text-gray-500">
                        {activeFilterCount > 0
                            ? 'No customer groups match these filters.'
                            : 'No customer groups yet — run the group build.'}
                    </p>
                </div>
            ) : (
                <div className="max-h-[70vh] overflow-auto">
                    <table className="w-full table-fixed text-sm" style={{ minWidth: totalWidth }}>
                        <colgroup>
                            {COLS.map(c => <col key={c.id} style={{ width: widthOf(c.id) }} />)}
                        </colgroup>
                        <thead>
                            <tr>
                                {COLS.map(c => c.sort ? (
                                    <SortableHeader
                                        key={c.id}
                                        label={c.label}
                                        sortKey={c.sort}
                                        sort={sortState}
                                        onSort={() => { if (!shouldSuppressClick()) onSort(c.sort!); }}
                                        align={c.align}
                                        onResizeStart={x => startResize(c.id, x)}
                                        onResetWidth={() => resetWidth(c.id)}
                                        isResizing={resizingKey === c.id}
                                    />
                                ) : (
                                    <th key={c.id}
                                        className={`sticky top-0 z-10 relative bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                                        {c.label}
                                        {c.id !== 'expand' && (
                                            <span className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                                                onMouseDown={e => { e.preventDefault(); startResize(c.id, e.clientX); }}
                                                onDoubleClick={() => resetWidth(c.id)} />
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const open = expandedId === r.groupId;
                                const blocked = Boolean(r.blockedSplit);
                                const overdue = Boolean(r.overdueSplit);
                                const companies = parseCompanyCodes(r.companyCodes);
                                return (
                                    <Fragment key={r.groupId}>
                                        <tr
                                            onClick={() => setExpandedId(open ? null : r.groupId)}
                                            className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${
                                                blocked || overdue ? 'bg-amber-50/40' : ''
                                            }`}
                                        >
                                            <td className="px-2 py-2 text-gray-400">
                                                {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-1">
                                                    {blocked && (
                                                        <span title="One company has this account blocked while another can still trade with it">
                                                            <Ban className="h-4 w-4 text-red-500" />
                                                        </span>
                                                    )}
                                                    {overdue && (
                                                        <span title={`One company holds items more than ${overdueDays} days overdue while another carries the customer clear`}>
                                                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                                                        </span>
                                                    )}
                                                    {!blocked && !overdue && <span className="text-xs text-gray-300">—</span>}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="truncate text-gray-800" title={r.displayName ?? undefined}>
                                                    {r.displayName ?? '—'}
                                                </div>
                                                <div className="truncate font-mono text-[10px] text-gray-400">
                                                    {r.groupKey}{r.registeredUid ? ` · ${r.registeredUid}` : ''}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex flex-wrap gap-1">
                                                    {companies.map(c => (
                                                        <span key={c} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-600">
                                                            {c}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-700">{r.memberCount}</td>
                                            <td className="px-3 py-2 text-right text-gray-800">
                                                {Number(r.openAmountEur) > 0 ? eur(r.openAmountEur) : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className={`px-3 py-2 text-right ${Number(r.overdueAmountEur) > 0 ? 'font-medium text-amber-800' : 'text-gray-300'}`}>
                                                {Number(r.overdueAmountEur) > 0 ? eur(r.overdueAmountEur) : '—'}
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-700">
                                                {Number(r.revenueEur) > 0 ? eur(r.revenueEur) : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-600">
                                                {Number(r.dunningLevelSent) > 0
                                                    ? <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700">L{r.dunningLevelSent}</span>
                                                    : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-500">
                                                {r.lastMovement ? formatDate(r.lastMovement) : <span className="text-gray-300">never</span>}
                                            </td>
                                            <td className="px-3 py-2 text-right text-xs text-gray-600">
                                                {Number(r.hubspotCompanies) > 0 ? r.hubspotCompanies : <span className="text-gray-300">—</span>}
                                            </td>
                                        </tr>
                                        {open && (
                                            <tr>
                                                <td colSpan={COLS.length} className="p-0">
                                                    <GroupDetailPanel groupId={r.groupId} />
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
                {total.toLocaleString('de-CH')} customer group{total === 1 ? '' : 's'}
            </div>
        </div>
    );
}
